import {
  FirebaseActionTypes,
  FirebaseActions,
  LanguageActions,
  LanguageSaga,
  LanguageSelectors,
  StatsActionTypes,
  StatsActions,
  StatsSaga,
  StatsSelectors,
  preloadStatsSaga,
} from '@web_opxp/features';
import { all, call, fork, put, select, spawn, take, takeEvery, throttle } from 'redux-saga/effects';
import { ApiService } from 'web_core_library';
import { AuthActionTypes, AuthActions, AuthSelectors } from '../../../auth';
import * as AnalyticsActions from '../../../core/actions/analyticsActions';
import { NEWSLETTER_STAT } from '../../../core/constants';
import * as ENV from '../../../core/env';
import { Action } from '../../../core/types';
import history, { getRefreshTokenHandler } from '../../../services/historyService';
import urlManager, { redirectBack } from '../../../services/urlManager';
import { SocialActionTypes } from '../../../social';
import * as ActionTypes from './actionTypes';
import * as Actions from './actions';
import { getLoginFormState } from './selectors';

export function* handleAppStart() {
  yield call(ApiService.init, ENV.ApiUrl);
  // check if url has changed since first app start
  urlManager.reload();
  if (!urlManager.backUrlSecure) {
    yield put(Actions.showLoginFormErrorAction({ error: 'SecurityAlert', field: 'all' }));
    return;
  }

  const isLanguageLoading = yield select(LanguageSelectors.isLoading);
  if (isLanguageLoading) yield take(LanguageActions.updateLanguage);
  // init firebase
  yield put(
    FirebaseActions.firebaseInitAction(
      ENV.FirebaseConfig.apiKey,
      ENV.FirebaseConfig.projectId,
      ENV.FirebaseConfig.appId,
      ENV.FirebaseConfig.messagingSenderId,
      ENV.FirebaseConfig.measurementId,
      urlManager.getUrlSearchParams()
    )
  );
  yield take(FirebaseActionTypes.FIREBASE_INITIALIZED);
  yield put(FirebaseActions.firebaseStartAction({ analytics: true, config: true, messaging: false }));
  yield take(FirebaseActionTypes.FIREBASE_READY);

  yield put(AuthActions.authRestoreAction());
  const result = yield take([
    AuthActionTypes.AUTH_RESTORE_SUCCESS,
    AuthActionTypes.AUTH_RESTORE_FAIL,
    AuthActionTypes.AUTH_SESSION_EXPIRED,
  ]);
  if (result.type === AuthActionTypes.AUTH_SESSION_EXPIRED) {
    yield put(Actions.showLoginFormErrorAction({ field: 'all', error: 'common:Errors.SessionExpired' }));
    yield fork(showLoginForm);
    return;
  }
  const session = yield select(AuthSelectors.getToken);

  if (!session || result.type === AuthActionTypes.AUTH_RESTORE_FAIL) {
    yield fork(showLoginForm);
    return;
  }

  const userId = yield select(AuthSelectors.getUserId);
  yield call(ApiService.setUserData, userId, session, getRefreshTokenHandler(false));
  try {
    const verified = yield select(AuthSelectors.isUserVerified);
    if (verified) {
      yield put(StatsActions.initStatsAction(userId));
      yield take([StatsActionTypes.STATS_READY]);
      yield call(preloadStatsSaga);
      const validated = yield select(StatsSelectors.getStatBooleanValueById, NEWSLETTER_STAT);
      if (validated) {
        yield call(redirectBack, session);
        return;
      }
    }
    history.push(urlManager.getSignupUrl());
    return;
  } catch (error) {
    const field = 'all';
    yield put(Actions.showLoginFormErrorAction({ error, field }));
    yield fork(showLoginForm);
  }
}

export function* showLoginForm() {
  yield put(Actions.showLoginFormAction());
}

// Regex for accepting emails that support unicode
// const EMAIL_REGEX = /^(([^<>()\[\]\.,;:\s@\"]+(\.[^<>()\[\]\.,;:\s@\"]+)*)|(\".+\"))@(([^<>()[\]\.,;:\s@\"]+\.)+[^<>()[\]\.,;:\s@\"]{2,})$/i; // eslint-disable-line

export function validateEmail(email: string) {
  // const regex = new RegExp(EMAIL_REGEX);
  return true;
  // return regex.test(email);
}

export function* validateEmailField(email: string) {
  if (!email) {
    yield put(AnalyticsActions.LoginNoEmailProvidedAction());
    yield put(Actions.showLoginFormErrorAction({ field: 'email', error: 'common.auth:Login.Errors.Email.Empty' }));
  } else if (!validateEmail(email)) {
    yield put(AnalyticsActions.LoginInvalidEmailProvidedAction());
    yield put(Actions.showLoginFormErrorAction({ field: 'email', error: 'common.auth:Login.Errors.Email.Invalid' }));
  } else {
    yield put(Actions.cleanLoginFormErrorAction('email'));
  }
}

export function* validatePasswordField(password: string) {
  // we could implement more validation rules for password field here
  if (!password) {
    yield put(AnalyticsActions.LoginNoPasswordProvidedAction());
    yield put(
      Actions.showLoginFormErrorAction({ field: 'password', error: 'common.auth:Login.Errors.Password.Empty' })
    );
  } else {
    yield put(Actions.cleanLoginFormErrorAction('password'));
  }
}

export function* formValidationHandler(action: Action) {
  switch (action.type) {
    case ActionTypes.APP_LOGIN_EMAIL_UPDATE: {
      const { email } = action.payload;
      yield fork(validateEmailField, email);
      break;
    }
    case ActionTypes.APP_LOGIN_PASSWORD_UPDATE: {
      const { password } = action.payload;
      yield fork(validatePasswordField, password);
      break;
    }
  }
  yield put(Actions.cleanLoginFormErrorAction('all'));
}

export function* submitLogin() {
  const { email, password } = yield select(getLoginFormState);
  yield fork(validateEmailField, email);
  const emailValidationResult = yield take([ActionTypes.APP_LOGIN_ERROR, ActionTypes.APP_LOGIN_ERROR_CLEAN]);
  yield fork(validatePasswordField, password);
  const passwordValidationResult = yield take([ActionTypes.APP_LOGIN_ERROR, ActionTypes.APP_LOGIN_ERROR_CLEAN]);
  if (
    emailValidationResult.type !== ActionTypes.APP_LOGIN_ERROR &&
    passwordValidationResult.type !== ActionTypes.APP_LOGIN_ERROR
  ) {
    yield put(AuthActions.loginAction(email, password));
    const result = yield take([AuthActionTypes.AUTH_LOGIN_SUCCESS, AuthActionTypes.AUTH_LOGIN_FAIL]);
    if (result.type === AuthActionTypes.AUTH_LOGIN_FAIL) {
      const error = result.error;
      const field = 'all';
      yield put(AnalyticsActions.LoginAuthErrorAction(error));
      yield put(Actions.showLoginFormErrorAction({ error, field }));
    } else {
      yield put(Actions.successLoginFormAction());
      yield spawn(handleAppStart);
    }
  }
}

export function* showErrorOnFailedSocialLoginSaga({ reason }: SocialActionTypes.ILoginSocialFailAction) {
  yield put(Actions.showLoginFormErrorAction({ field: 'all', error: reason || 'common.auth:Login.Errors.Social' }));
}

export function* socialLoginCompletedSaga({ accessToken }: SocialActionTypes.IRestoreSocialSuccessAction) {
  yield put(AuthActions.validateTokenAction(accessToken, true));
  const result = yield take([AuthActionTypes.AUTH_VALIDATE_TOKEN_SUCCESS, AuthActionTypes.AUTH_VALIDATE_TOKEN_FAIL]);
  let userId = 0;
  if (result.type === AuthActionTypes.AUTH_VALIDATE_TOKEN_SUCCESS) {
    userId = yield select(AuthSelectors.getUserId);
  }
  if (!userId) {
    yield fork(showLoginForm);
    return;
  }
  const verified = yield select(AuthSelectors.isUserVerified);
  if (!verified) {
    history.push(urlManager.getSignupUrl());
  }

  yield spawn(handleAppStart);
}

export function* appWatcher() {
  yield all([
    takeEvery(LanguageActions.initLanguage, handleAppStart),
    throttle(1000, [ActionTypes.APP_LOGIN_EMAIL_UPDATE, ActionTypes.APP_LOGIN_PASSWORD_UPDATE], formValidationHandler),
    takeEvery(ActionTypes.APP_LOGIN_SUBMIT, submitLogin),
    takeEvery(SocialActionTypes.SOCIAL_LOGIN_FAIL, showErrorOnFailedSocialLoginSaga),
    takeEvery(SocialActionTypes.SOCIAL_RESTORE_SUCCESS, socialLoginCompletedSaga),
  ]);
}

// registers all watchers
export default function* rootSaga() {
  yield all([fork(appWatcher), fork(StatsSaga), spawn(LanguageSaga)]);
}
